import { Button } from 'components/atoms';
import { Card } from 'components/molecules';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelDigitalDelivery,
  useGetDigitalDelivery,
  usePostDigitalDelivery,
  usePutDigitalDelivery,
} from 'services/digital-delivery';
import { AsapType, DELIVERY_METHOD, IDigitalDeliveryBase } from 'services/digital-delivery/types';
import { DELIVERY_STATUS, SUBSCRIPTION_TYPE } from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDateToInput } from 'utils/global/date.utils';
import { isEmptyId } from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import DigitalDeliveryForm from '../../digitalDelivery/DigitalDeliveryForm';

const MusicProductDigitalDeliveryDetail = () => {
  const productId = useCustomParamId('id', '/music-product-digital');
  const salesChannelId = useCustomParamId('salesChannelId', '/music-product-digital');
  const providerId = useCustomParamId('providerId', '/music-product-digital');

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { values, setFieldValue }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const digitalDelivery = useGetDigitalDelivery(productId, salesChannelId, providerId);

  const createDigitalDelivery = usePostDigitalDelivery();
  const updateDigitalDelivery = usePutDigitalDelivery();

  const cancelDigitalDelivery = useCancelDigitalDelivery();

  const onSubmitDelivery = async (actions: PopupActions | null) => {
    if (values.delivery) {
      const delivery = middleware.digitalDelivery.formToQuery(values.delivery, {
        salesChannelId,
        productId,
        providerId,
      });

      if (isEmptyId(values.delivery.id)) {
        return await createDigitalDelivery.mutateAsync(delivery, {
          onSuccess: async () => {
            await digitalDelivery.refetch();
            actions?.close();
          },
          onError: async (error) => onErrorShowAllErrorsToast(error),
        });
      }

      return await updateDigitalDelivery.mutateAsync(delivery, {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions?.close();
        },
        onError: async (error) => onErrorShowAllErrorsToast(error),
      });
    }
  };

  const onCancelDelivery = async (actions: PopupActions) => {
    await cancelDigitalDelivery.mutateAsync(
      {
        productId,
        salesChannel: salesChannelId,
        providerId,
      },
      {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions.close();
        },
        onError: async (error) => onErrorShowAllErrorsToast(error),
      },
    );
  };

  const { minPublishDate, isReleaseDateValid } = useMemo(() => {
    const today = new Date();
    const minDate = new Date();
    minDate.setDate(today.getDate() + 10);

    const isValid = values.releaseDate
      ? new Date(values.releaseDate).getTime() >= minDate.getTime()
      : false;

    return {
      minPublishDate: formatDateToInput(minDate),
      isReleaseDateValid: isValid,
    };
  }, [values.releaseDate]);

  const initialCountry = useMemo<IDigitalDeliveryBase>(
    () => ({
      countryCode: '',
      asap: AsapType.AsapToDate,
      publishDate: minPublishDate,
      publishTime: '00:00',
      endDate: null,
      endTime: null,
      isTimeZoneBased: true,
      subscriptionTypeId: SUBSCRIPTION_TYPE.all,
    }),
    [minPublishDate],
  );

  useEffect(() => {
    if (digitalDelivery.data && values.mainTitle.title) {
      let initialDelivery = middleware.digitalDelivery.queryToForm(digitalDelivery.data);

      if (digitalDelivery.data.status === DELIVERY_STATUS.notSend) {
        // przypisywanie initial dostawy z backu ale na teraz
        initialDelivery.included = [{ ...initialCountry, countryCode: 'ALL' }];
      }
      setFieldValue('delivery', initialDelivery);
    }
  }, [digitalDelivery.data, values.mainTitle.title]);

  return (
    <div className="flex flex-col w-full max-w-[500px] gap-6 pt-12">
      <Button
        fullWidth
        variant={BUTTON_VARIANT.white}
        onClick={() => navigate(`/music-product-digital/${productId}/delivery`)}
      >
        <Button.Icon icon={<PiCaretLeft />} />
        <Button.Text text={t('button.back')} />
      </Button>
      <Card.Delivery
        service={{
          salesChannelId: digitalDelivery.data?.salesChannelId ?? '',
          salesChannelName: digitalDelivery.data?.salesChannelName ?? '-',
          logoImageUrl: digitalDelivery.data?.salesChannelLogo ?? '',
          providerId: digitalDelivery.data?.providerId ?? '',
          providerName: digitalDelivery.data?.providerName ?? '-',
          deliveryMethod: digitalDelivery.data?.deliveryMethod ?? DELIVERY_METHOD.unknown,
          deliveryStatus: digitalDelivery.data?.status ?? DELIVERY_STATUS.notSend,
        }}
      />
      <DigitalDeliveryForm
        onCancelDelivery={onCancelDelivery}
        onSubmitDelivery={onSubmitDelivery}
        initialCountry={initialCountry}
        initialValues={digitalDelivery.data}
        values={values.delivery}
        isReleaseDateValid={isReleaseDateValid}
        minPublishDate={minPublishDate}
      />
    </div>
  );
};

export default MusicProductDigitalDeliveryDetail;
